import * as Api from '@ViewModels';
import { useMemo } from 'react';
import { useAIConversationChatMessageQuery } from '../AIConversation/useAIConversationChatMessageQuery';
import { TimelineRow, mergeAdherenceTimeline } from './mergeAdherenceTimeline';

interface QueryParams {
	phoneCall: Api.IPhoneCall;
	speakerLines: Api.ISpeakerLine[] | undefined;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

export interface CallAdherenceTimelineResult {
	data: TimelineRow[] | undefined;
	isLoading: boolean;
	error: unknown;
}

export function useCallAdherenceTimelineQuery({
	phoneCall,
	speakerLines,
	enabled = true,
	impersonationContext,
}: QueryParams): CallAdherenceTimelineResult {
	const aiConversationId = phoneCall.aiConversationId;
	const aiQueryEnabled = Boolean(enabled && aiConversationId);

	const aiQuery = useAIConversationChatMessageQuery({
		id: aiConversationId ?? '',
		enabled: aiQueryEnabled,
		impersonationContext,
	});

	const data = useMemo<TimelineRow[] | undefined>(() => {
		if (aiQueryEnabled && aiQuery.isLoading) return undefined;
		return mergeAdherenceTimeline({
			speakerLines,
			aiMessages: aiQueryEnabled ? aiQuery.data : [],
			callStartTime: phoneCall.startTime,
			callDuration: phoneCall.duration,
		});
	}, [aiQueryEnabled, aiQuery.isLoading, aiQuery.data, speakerLines, phoneCall.startTime, phoneCall.duration]);

	const isLoading = data === undefined && !aiQuery.error;
	const error = aiQueryEnabled ? aiQuery.error : null;
	return { data, isLoading, error };
}
